import type { PersonSocialValuationResponse } from '@on3/api';
import {
  SocialIcon,
  type SocialIconType,
} from '@on3/ui-lib/components/SocialIcon';
import {
  formatMillions,
  formatSocialHandle,
} from '@on3/ui-lib/src/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { Link } from 'components/Link';
import useAthlete from 'contexts/AthleteProvider';
import { useCallback } from 'react';
import { socialLink } from 'utils/social';

import styles from './NilSocialValuations.module.scss';

interface ValuationsProps {
  socialValuations?: PersonSocialValuationResponse[];
  rank?: number | null;
  groupRank?: number | null;
}

export const NilSocialValuations = ({
  socialValuations,
  rank,
  groupRank,
}: ValuationsProps) => {
  const { athleteInHighSchool, athlete } = useAthlete();

  const isInCollege = athlete?.organizationLevel === 'College';
  const defaultSport = athlete?.defaultSport?.name;
  const showRank = !!rank && rank <= 100;
  const showGroupRank = !!groupRank;
  const rankingsLink = `/nil/rankings/player/${
    athleteInHighSchool ? 'high-school' : 'college'
  }/${slugify(defaultSport)}/`;
  const nil100Link = '/nil/rankings/player/nil-100/';

  const totalFollowers: number =
    socialValuations
      ?.map(({ followers }) => followers ?? 0)
      .filter(Boolean)
      .reduce((acc: number, followers: number) => acc + followers, 0) ?? 0;

  const showFollowers = totalFollowers > 0;

  const getRankGroup = useCallback(() => {
    if (athleteInHighSchool) {
      return `HS ${defaultSport} NIL Rank`;
    } else if (isInCollege) {
      return `College ${defaultSport} NIL Rank`;
    }

    return `NIL Rank`;
  }, [defaultSport, isInCollege, athleteInHighSchool]);

  return (
    <ul className={styles.block}>
      {showRank && (
        <li className={styles.rank}>
          <span>NIL 100</span>
          <div>
            <Link href={nil100Link}>{rank ? `#${rank}` : '-'}</Link>
          </div>
        </li>
      )}

      {showGroupRank && (
        <li className={styles.rank}>
          <span>{getRankGroup()}</span>
          <div>
            <Link href={rankingsLink}>{groupRank ? `#${groupRank}` : '-'}</Link>
          </div>
        </li>
      )}

      {showFollowers && (
        <li className={styles.followers}>
          <span>Followers</span>
          <div className={styles.total}>
            {formatMillions(totalFollowers) || '-'}
          </div>
        </li>
      )}

      {(socialValuations ?? []).map(({ profile, followers }, index) => {
        const { handle, type } = profile || {};

        if (!type || !handle) {
          return null;
        }

        return (
          <li key={index}>
            <span>{type}</span>
            <div>
              <Link className={styles.social} href={socialLink(type, handle)}>
                <SocialIcon type={type as SocialIconType} />
                <div className={styles.meta}>
                  <span className={styles.handle}>
                    {formatSocialHandle(profile?.handle ?? '')}
                  </span>
                  {followers && followers > 0 && (
                    <span className={styles.followCount}>
                      {`(${formatMillions(followers)})`}
                    </span>
                  )}
                </div>
              </Link>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
